import React, { useState, useRef, useEffect } from 'react';
import ProblemSolver from '../components/ProblemSolver';
import QuestionHistory from '../components/QuestionHistory';
import SubscriptionModal from '../components/SubscriptionModal';
import RightSideMenu from '../components/RightSideMenu';
import CreditStatus from '../components/CreditStatus';
import UnifiedHeader from '../components/UnifiedHeader';
import { authService } from '../services/authService';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { getCreditStatus } from '../services/api';
import { AlertCircle } from 'lucide-react';
import './PixelAI.css';

function PixelAI() {
  const { language } = useLanguage();
  const t = translations[language];
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showRightMenu, setShowRightMenu] = useState(false);
  const creditStatusRef = useRef(null);
  const user = authService.getUser();
  const [creditStatus, setCreditStatus] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  useEffect(() => {
    loadCreditStatus();
    
    const handleCreditChange = () => {
      console.log('💎 Credit status change event received in PixelAI');
      loadCreditStatus();
      setRefreshTrigger(prev => prev + 1);
    };
    
    window.addEventListener('creditStatusChanged', handleCreditChange);
    
    return () => {
      window.removeEventListener('creditStatusChanged', handleCreditChange);
    };
  }, []);

  const loadCreditStatus = async () => {
    try {      
      const status = await getCreditStatus();
      console.log('💰 PixelAI credit status loaded:', status);
      setCreditStatus(status);
      
      if (creditStatusRef.current && typeof creditStatusRef.current.refresh === 'function') {
        creditStatusRef.current.refresh();
      }
    } catch (err) {
      console.error('Error loading credit status:', err);
    }
  };

  const handleOpenSubscription = () => {
    setShowSubscriptionModal(true);
    document.body.classList.add('modal-open');
  };

  const handleCloseSubscription = () => {
    setShowSubscriptionModal(false);
    document.body.classList.remove('modal-open');
  };

  const handleSelectPlan = (plan) => {
    console.log('Selected plan:', plan);
  };

  return (
    <div className="pixel-ai-view">
      <UnifiedHeader
        onOpenMenu={() => setShowRightMenu(true)}
        rightContent={
          <CreditStatus
            key={refreshTrigger}
            ref={creditStatusRef}
            compact={true}
            onUpgrade={handleOpenSubscription}
          />
        }
      />

      {/* Main Content */}
      <main className="pixel-main">
        <div className="pixel-container">
          {/* Transparency Notice */}
          <div className="pixel-transparency-notice">
            <div className="notice-icon-small">
              <AlertCircle size={16} />
            </div>
            <div className="notice-content-small">
              <p className="notice-text-small">
                {t.pixelAI?.transparencyText || 'PIXEL AI assists with learning and problem-solving. Always verify solutions with textbooks, teachers, or official materials before relying on them for exams.'}
              </p>
            </div>
          </div>

          {/* Problem Solver Section */}
          <section className="pixel-section">
            <div className="section-content">
              <ProblemSolver />
            </div>
          </section>

          {/* Question History Section */}
          <section id="history-section" className="pixel-section">
            <div className="section-content">
              <QuestionHistory />
            </div>
          </section>
        </div>
      </main>

      {/* Right Side Menu */}
      <RightSideMenu
        isOpen={showRightMenu}
        onClose={() => setShowRightMenu(false)}
        user={user}
        currentPlan={creditStatus?.subscription_plan || 'free'}
        onOpenSubscription={handleOpenSubscription}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={handleCloseSubscription}
        currentPlan={creditStatus?.subscription_plan || 'free'}
        onSelectPlan={handleSelectPlan}
      />
    </div>
  );
}

export default PixelAI;